/**
 * PuzzlePlayPage — Solve-Hop Maze (v3 · fixed-grid layout · trap dead-ends).
 *
 * The user's spec (Phase 2.24):
 *  - Fixed 3-row × 4-col grid. Start at top-left, Finish at bottom-right.
 *  - Every on-path cell has 2-3 outgoing strips going only RIGHT, DIAGONAL-DOWN-RIGHT, or DOWN.
 *  - One option per cell is correct; the others are decoys leading to dead-end "Oops!" trap cells.
 *  - No going back. Picking ANY strip advances you. Wrong picks land you in a trap (no outgoing edges).
 *  - The only way to reach Finish is to pick correctly on every on-path cell.
 *  - Reaching Finish via the correct path → fireworks + Stage Cleared.
 *  - Landing in a trap → "Oops! Restart and try again" overlay.
 *
 * No criss-cross, no overlap. Strip directions: horizontal (right), vertical (down),
 * 45° (diag-down-right). All cells positioned by their (row, col) from the backend.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { apiGet, apiPost } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, RotateCcw, Trophy, Star, Zap, Loader2, Sparkles, Lightbulb, X, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTutorBuddy } from '@/components/TutorBuddy';

// ---------- Layout constants (units: pixels of the SVG viewBox) ----------
const CELL_W = 200;
const CELL_H = 150;
const COL_GAP = 80;        // horizontal space between columns (where right-strips travel)
const ROW_GAP = 80;        // vertical space between rows (where down-strips travel)
const PAD = 30;

const CELL_PAINT = {
  orange:  { fill: '#FED7AA', stroke: '#C2410C', text: '#7C2D12' },
  blue:    { fill: '#BAE6FD', stroke: '#0369A1', text: '#0C4A6E' },
  green:   { fill: '#BBF7D0', stroke: '#15803D', text: '#14532D' },
  yellow:  { fill: '#FEF08A', stroke: '#A16207', text: '#713F12' },
  red:     { fill: '#FECACA', stroke: '#B91C1C', text: '#7F1D1D' },
  pink:    { fill: '#FBCFE8', stroke: '#BE185D', text: '#831843' },
  teal:    { fill: '#99F6E4', stroke: '#0F766E', text: '#134E4A' },
  purple:  { fill: '#E9D5FF', stroke: '#7E22CE', text: '#581C87' },
};

// Color palette for active strips (inherited from cell color)
const STRIP_PAINT = ['#FB923C', '#38BDF8', '#34D399', '#FACC15', '#F87171', '#F472B6', '#A78BFA'];

function cellTopLeft(row, col) {
  return {
    x: PAD + col * (CELL_W + COL_GAP),
    y: PAD + row * (CELL_H + ROW_GAP),
  };
}
function cellCenter(row, col) {
  const tl = cellTopLeft(row, col);
  return { x: tl.x + CELL_W / 2, y: tl.y + CELL_H / 2 };
}

function fireworksAt(x, y, intensity = 'normal') {
  const opts = {
    small:  { particleCount: 35,  spread: 50,  startVelocity: 28, decay: 0.92, scalar: 0.85 },
    normal: { particleCount: 95,  spread: 95,  startVelocity: 38, decay: 0.92, scalar: 1.0 },
    big:    { particleCount: 230, spread: 130, startVelocity: 55, decay: 0.91, scalar: 1.2 },
  }[intensity];
  confetti({
    ...opts,
    origin: { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) },
    colors: ['#F59E0B', '#FB7185', '#10B981', '#6366F1', '#EC4899', '#FBBF24', '#A78BFA'],
  });
  if (intensity !== 'small') {
    setTimeout(() => confetti({
      ...opts,
      particleCount: Math.floor(opts.particleCount * 0.6),
      origin: { x: x + 0.05, y: y - 0.05 },
      colors: ['#FCD34D', '#F472B6', '#34D399', '#A78BFA', '#FB923C'],
    }), 180);
  }
}

export default function PuzzlePlayPage() {
  const { puzzleId } = useParams();
  const [searchParams] = useSearchParams();
  const isDailyChallenge = searchParams.get('daily') === '1';
  const navigate = useNavigate();
  const buddy = useTutorBuddy();
  const { user } = useAuth();
  // Basic users only get Gamify as a free *taster* via the Daily Challenge.
  // Show a soft upsell banner when they're playing one, so they understand
  // this is a preview of what Growth/Elite gets daily.
  const isBasicTaster = (
    isDailyChallenge
    && (user?.subscription_tier || 'basic').toLowerCase() === 'basic'
    && !['admin', 'support_staff'].includes((user?.role || '').toLowerCase())
  );

  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [visitedPath, setVisitedPath] = useState([]);
  const [traversedEdges, setTraversedEdges] = useState([]);
  const [wrongClicks, setWrongClicks] = useState(0);
  const [moves, setMoves] = useState(0);
  const [shakeEdgeId, setShakeEdgeId] = useState(null);
  const [hintNode, setHintNode] = useState(null);
  const [completed, setCompleted] = useState(null);
  const startRef = useRef(Date.now());
  const completedRef = useRef(false);

  useEffect(() => {
    apiGet(`/puzzles/${puzzleId}`)
      .then(res => {
        const p = res?.puzzle;
        if (!p) throw new Error('Puzzle missing');
        setPuzzle(p);
        const start = (p.nodes || []).find(n => n.is_start) || (p.nodes || [])[0];
        if (start) {
          setCurrentNodeId(start.id);
          setVisitedPath([start.id]);
        }
        startRef.current = Date.now();
      })
      .catch(e => {
        if (e?.message?.includes('upgrade_required') || e?.status === 403) {
          navigate('/gamify');
          return;
        }
        setErr(e?.message || 'Could not load puzzle.');
      })
      .finally(() => setLoading(false));
  }, [puzzleId, navigate]);

  const nodeMap = useMemo(() => {
    const m = {};
    (puzzle?.nodes || []).forEach(n => { m[n.id] = n; });
    return m;
  }, [puzzle]);
  const currentNode = nodeMap[currentNodeId];
  const rows = puzzle?.rows || 3;
  const cols = puzzle?.cols || 4;
  const boardW = PAD * 2 + cols * CELL_W + (cols - 1) * COL_GAP;
  const boardH = PAD * 2 + rows * CELL_H + (rows - 1) * ROW_GAP;

  // Outgoing edges from current cell (used for click validation + visual emphasis)
  const outgoingFromCurrent = useMemo(() => {
    if (!puzzle || !currentNodeId) return new Set();
    return new Set((puzzle.edges || []).filter(e => e.from === currentNodeId).map(e => e.id));
  }, [puzzle, currentNodeId]);

  // Per-edge traversal classification:
  //  - traversedRight: edges the kid clicked that were correct
  //  - traversedWrong: edges the kid clicked that were decoys
  //  - revealedRight:  the correct edges from cells where the kid picked wrong
  //                    (we light these green so they SEE which strip was right)
  const { traversedRight, traversedWrong, revealedRight } = useMemo(() => {
    const right = new Set();
    const wrong = new Set();
    const revealed = new Set();
    if (!puzzle) return { traversedRight: right, traversedWrong: wrong, revealedRight: revealed };
    const edgesById = {};
    (puzzle.edges || []).forEach(e => { edgesById[e.id] = e; });
    traversedEdges.forEach(eid => {
      const e = edgesById[eid];
      if (!e) return;
      if (e.is_correct_edge) {
        right.add(eid);
      } else {
        wrong.add(eid);
        // find the correct edge from the same source cell — reveal it green
        const correct = (puzzle.edges || []).find(ee => ee.from === e.from && ee.is_correct_edge);
        if (correct) revealed.add(correct.id);
      }
    });
    return { traversedRight: right, traversedWrong: wrong, revealedRight: revealed };
  }, [puzzle, traversedEdges]);
  const handleEdgeClick = (edge, mouseEvt) => {
    if (completed) return;
    if (!currentNode || currentNode.is_finish) return;
    // Only edges leaving the current cell can be picked.
    if (edge.from !== currentNodeId) {
      toast.message("That strip leaves a different cell — focus on YOUR current one.", { duration: 1500 });
      return;
    }
    setMoves(m => m + 1);
    const isCorrect = !!edge.is_correct_edge;

    apiPost('/puzzles/move', {
      puzzle_id: puzzleId, from_node: currentNode.id, edge_id: edge.id, correct: isCorrect,
    }).catch(() => {});

    setTraversedEdges(t => [...t, edge.id]);
    const nextNodeId = edge.to;
    const nextNode = nodeMap[nextNodeId];
    setCurrentNodeId(nextNodeId);
    setVisitedPath(p => [...p, nextNodeId]);

    if (isCorrect) {
      const x = mouseEvt ? mouseEvt.clientX / window.innerWidth : 0.5;
      const y = mouseEvt ? mouseEvt.clientY / window.innerHeight : 0.5;
      fireworksAt(x, y, 'normal');
      buddy?.flash?.('happy', 1500);
    } else {
      setWrongClicks(w => w + 1);
      setShakeEdgeId(edge.id);
      setTimeout(() => setShakeEdgeId(null), 700);
      buddy?.console?.('Hmm, not quite. But keep going — every cell has a problem to solve!');
      toast.message("That wasn't the right answer — but you can still reach the treasure! Solve the next one.", { duration: 2200 });
    }
    // Reaching Finish triggers the celebration regardless of route taken.
    if (nextNode?.is_finish) {
      setTimeout(() => finishPuzzle(), 350);
    }
  };

  const finishPuzzle = async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    const dur = (Date.now() - startRef.current) / 1000;
    try {
      const completeEndpoint = isDailyChallenge
        ? '/puzzles/daily-challenge/complete'
        : '/puzzles/complete';
      const res = await apiPost(completeEndpoint, {
        puzzle_id: puzzleId, moves, wrong_clicks: wrongClicks, duration_seconds: dur,
      });
      fireworksAt(0.5, 0.5, 'big');
      setTimeout(() => fireworksAt(0.2, 0.4, 'big'), 250);
      setTimeout(() => fireworksAt(0.8, 0.4, 'big'), 500);
      buddy?.celebrate?.(isDailyChallenge ? "Daily challenge cleared! Massive Veda Coins!" : 'You did it! Treasure unlocked! 🏆');
      setCompleted({ ...res, duration: dur, isDailyChallenge });
      apiGet('/puzzles/my-stats').then(s => setXpBalance(s.xp_balance ?? 0)).catch(() => {});
    } catch (e) {
      fireworksAt(0.5, 0.5, 'big');
      setCompleted({ xp_awarded: puzzle?.xp_award || 0, perfect: wrongClicks === 0, duration: dur });
    }
  };

  // Hints now deduct Veda Coins. Load user's current balance + cost on mount.
  const [xpBalance, setXpBalance] = useState(null);
  const [hintCost, setHintCost] = useState(5);
  const [hintRevealed, setHintRevealed] = useState(null);   // {node_id, solution}
  const [hintLoading, setHintLoading] = useState(false);

  useEffect(() => {
    apiGet('/puzzles/my-stats').then(s => {
      setXpBalance(s.xp_balance ?? 0);
      setHintCost(s.hint_cost_xp ?? 5);
    }).catch(() => {});
  }, []);

  const showHint = async () => {
    if (!currentNode || currentNode.is_finish) return;
    if (hintLoading) return;
    // If already revealed for this cell, just show it again
    if (hintRevealed && hintRevealed.node_id === currentNode.id) {
      setHintNode(currentNode);
      return;
    }
    setHintLoading(true);
    try {
      const res = await apiPost('/puzzles/hint', {
        puzzle_id: puzzleId, node_id: currentNode.id,
      });
      setXpBalance(res.xp_balance_after);
      setHintRevealed({ node_id: currentNode.id, solution: res.solution });
      setHintNode(currentNode);
      buddy?.flash?.('thinking', 2000);
      toast.warning(`Hint used! −${res.cost_xp} Veda Coins (balance: ${res.xp_balance_after})`, { duration: 2500 });
    } catch (e) {
      const msg = e?.message || '';
      if (msg.includes('not_enough_xp') || msg.includes('need')) {
        toast.error(`Not enough Veda Coins for a hint! Earn some by solving puzzles first.`, { duration: 2800 });
      } else {
        toast.error(msg.replace(/^HTTP \d+:\s*/, '') || "Couldn't fetch a hint.");
      }
    } finally {
      setHintLoading(false);
    }
  };

  const canHint = xpBalance !== null && xpBalance >= hintCost;

  const restart = () => {
    completedRef.current = false;
    const start = (puzzle.nodes || []).find(n => n.is_start) || puzzle.nodes[0];
    setCurrentNodeId(start.id);
    setVisitedPath([start.id]);
    setTraversedEdges([]);
    setWrongClicks(0); setMoves(0); setCompleted(null);
    setHintRevealed(null);
    startRef.current = Date.now();
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <p className="font-body text-rose-600 mb-4">{err}</p>
        <Button onClick={() => navigate('/gamify')} variant="outline">Back to Gamify</Button>
      </div>
    );
  }
  if (!puzzle) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="puzzle-play-page">
      {isBasicTaster && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-rose-50 to-violet-50"
          data-testid="daily-challenge-basic-banner">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-heading uppercase tracking-wider text-amber-700">
            <Sparkles className="w-3.5 h-3.5" /> Free Taster
          </span>
          <span className="text-xs font-body text-slate-700 flex-1 min-w-[14rem]">
            You're playing today's Daily Challenge on us. Growth &amp; Elite get a fresh puzzle <strong>every day</strong>, plus full Gamify with all subjects.
          </span>
          <Button onClick={() => navigate('/subscription')}
            data-testid="daily-challenge-upgrade-cta"
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-heading">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Unlock daily access
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex-1 min-w-0">
          <button onClick={() => navigate('/gamify')}
            className="text-xs font-body text-slate-500 hover:text-rose-600 inline-flex items-center gap-1 mb-2"
            data-testid="puzzle-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> All puzzles
          </button>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">{puzzle.title}</h1>
          <p className="text-sm font-body text-slate-600 mt-1">{puzzle.intro}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-heading uppercase">
            <Star className="w-3 h-3" /> {puzzle.difficulty}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-heading">
            <Zap className="w-3 h-3" /> +{puzzle.xp_award} Veda Coins
          </span>
          <span data-testid="puzzle-xp-balance" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-[11px] font-heading">
            <Zap className="w-3 h-3" /> {xpBalance ?? '…'} Veda Coins
          </span>
          <span data-testid="puzzle-wrong-counter" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-heading">
            Wrong: {wrongClicks}
          </span>
          <Button variant="outline" size="sm" onClick={showHint}
            disabled={!canHint || hintLoading || currentNode?.is_finish}
            className={`font-heading text-xs ${canHint ? '' : 'opacity-60 cursor-not-allowed'}`}
            title={canHint ? `Reveal the answer for this cell · −${hintCost} Veda Coins` : `Earn ${hintCost} Veda Coins to unlock hints`}
            data-testid="puzzle-hint-btn">
            <Lightbulb className="w-3.5 h-3.5 mr-1" /> Hint <span className="ml-1 text-[10px] opacity-70">−{hintCost} Veda Coins</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={restart} className="font-heading text-xs" data-testid="puzzle-restart-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restart
          </Button>
        </div>
      </div>

      {/* Maze board */}
      <Card className="p-3 sm:p-5 bg-gradient-to-br from-amber-50/50 via-white to-rose-50/40 border-2 border-dashed border-amber-300/60 shadow-inner relative overflow-hidden" data-testid="puzzle-board">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(251,191,36,0.10) 0%, transparent 45%), radial-gradient(circle at 75% 75%, rgba(244,114,182,0.10) 0%, transparent 45%)' }} />

        <div className="relative overflow-x-auto">
          <svg
            viewBox={`0 0 ${boardW} ${boardH}`}
            preserveAspectRatio="xMidYMid meet"
            className="block mx-auto"
            style={{ width: '100%', maxWidth: boardW, height: 'auto', minWidth: 360 }}
            data-testid="puzzle-svg">
            <defs>
              <filter id="crayon-shadow" x="-10%" y="-10%" width="120%" height="120%">
                <feDropShadow dx="2" dy="3" stdDeviation="0.8" floodColor="#000" floodOpacity="0.18" />
              </filter>
            </defs>

            {/* Strips first so cells render on top */}
            {(puzzle.edges || []).map((e, i) => {
              const fromNode = nodeMap[e.from];
              const toNode = nodeMap[e.to];
              if (!fromNode || !toNode) return null;
              return (
                <PuzzleStrip
                  key={e.id}
                  edge={e}
                  fromNode={fromNode}
                  toNode={toNode}
                  paint={STRIP_PAINT[i % STRIP_PAINT.length]}
                  isOutgoingFromCurrent={outgoingFromCurrent.has(e.id) && !currentNode?.is_finish}
                  isShaking={shakeEdgeId === e.id}
                  traversalState={traversedRight.has(e.id) ? 'right' : traversedWrong.has(e.id) ? 'wrong' : revealedRight.has(e.id) ? 'revealed' : 'idle'}
                  onClick={(evt) => handleEdgeClick(e, evt)}
                />
              );
            })}

            {/* Cells */}
            {(puzzle.nodes || []).map(n => (
              <PuzzleCell
                key={n.id}
                node={n}
                isCurrent={currentNodeId === n.id}
                isVisited={visitedPath.includes(n.id) && currentNodeId !== n.id}
              />
            ))}
          </svg>
        </div>

        {/* Trail progress */}
        <div className="relative mt-4 flex items-center gap-3 px-2">
          <span className="text-[11px] font-heading text-slate-500 uppercase">Trail</span>
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-rose-400 to-orange-400 transition-all duration-500 rounded-full"
              style={{ width: `${puzzle.solution_path?.length ? Math.min(100, ((visitedPath.length - 1) / Math.max(1, puzzle.solution_path.length - 1)) * 100) : 0}%` }}
              data-testid="puzzle-progress-bar" />
          </div>
          <span className="text-[11px] font-mono text-slate-500" data-testid="puzzle-progress-text">
            {visitedPath.length - 1} / {Math.max(0, (puzzle.solution_path?.length || 1) - 1)}
          </span>
        </div>
      </Card>

      {/* How to play */}
      <Card className="p-3 bg-blue-50/50 border-blue-200">
        <p className="text-xs font-body text-blue-800 leading-relaxed">
          <span className="font-heading">How to play:</span> Solve every cell, hop on the answer-strip whose value matches your answer. Pick correctly every time for a Perfect run — but even with wrong picks, you'll keep solving and reach the treasure either way.
        </p>
      </Card>

      {/* Hint dialog */}
      <AnimatePresence>
        {hintNode && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setHintNode(null)}
            data-testid="puzzle-hint-overlay">
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              onClick={(ev) => ev.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-md shadow-2xl border-2 border-amber-300">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg font-bold text-amber-700 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" /> Quick hint
                </h3>
                <button onClick={() => setHintNode(null)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              <p className="text-sm font-body text-slate-700">
                You're on <span className="font-heading">{hintNode.label}</span>. The problem is:
              </p>
              <div className="mt-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-center font-mono text-base text-slate-800">
                {hintNode.problem}
              </div>
              {hintRevealed && hintRevealed.node_id === hintNode.id && (
                <div className="mt-3 p-3 rounded-xl bg-emerald-50 border-2 border-emerald-300 text-center" data-testid="hint-answer">
                  <p className="text-[10px] font-heading text-emerald-700 uppercase tracking-wide mb-1">
                    The answer is
                  </p>
                  <p className="font-display font-bold text-2xl text-emerald-700">{hintRevealed.solution}</p>
                </div>
              )}
              <p className="text-xs font-body text-slate-500 mt-3 italic">
                {hintRevealed ? 'Now click the matching strip on the board to advance.' : 'Solve it, then pick the strip whose value matches.'}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trap overlay removed in Phase 2.25 — every cell is now solvable, no dead-ends. */}

      {/* Completion overlay */}
      <AnimatePresence>
        {completed && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
            data-testid="puzzle-complete-overlay">
            <motion.div
              initial={{ scale: 0.5, rotate: -8, y: 40 }}
              animate={{ scale: 1, rotate: 0, y: 0 }}
              transition={{ type: 'spring', stiffness: 180, damping: 14 }}
              className="bg-gradient-to-br from-amber-50 via-white to-rose-50 rounded-3xl p-7 max-w-md shadow-2xl border-4 border-amber-300 relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none opacity-50"
                   style={{ background: 'radial-gradient(circle at 50% 0%, rgba(251,191,36,0.4) 0%, transparent 60%)' }} />
              <div className="relative text-center">
                <motion.div
                  animate={{ rotate: [0, -8, 8, -6, 6, 0], scale: [1, 1.15, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 1.5 }}
                  className="w-24 h-24 rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 mx-auto flex items-center justify-center shadow-xl mb-4">
                  <Trophy className="w-12 h-12 text-white drop-shadow-lg" strokeWidth={1.5} />
                </motion.div>
                <h2 className="font-display text-3xl font-bold bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">
                  Stage Cleared!
                </h2>
                <p className="text-sm font-body text-slate-600 mt-1">
                  You hopped through {visitedPath.length} cells in {Math.round(completed.duration)}s.
                </p>
                <div className="mt-5 grid grid-cols-3 gap-2">
                  <ResultPill icon={Zap} label="Veda Coins" value={`+${completed.xp_awarded || 0}`} accent="amber" testid="result-xp" />
                  <ResultPill icon={Star} label="Wrong" value={wrongClicks} accent="slate" testid="result-wrong" />
                  <ResultPill icon={Trophy} label="Status" value={completed.perfect ? 'PERFECT' : 'CLEARED'} accent={completed.perfect ? 'rose' : 'emerald'} testid="result-status" />
                </div>
                {completed.perfect_bonus > 0 && (
                  <p className="mt-3 text-xs font-heading text-emerald-700 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> +{completed.perfect_bonus} perfect-run bonus!
                  </p>
                )}
                <div className="mt-5 flex gap-2 justify-center">
                  <Button onClick={restart} variant="outline" className="font-heading text-xs" data-testid="result-restart-btn">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Play again
                  </Button>
                  <Button onClick={() => navigate('/gamify')} className="font-heading text-xs bg-gradient-to-r from-rose-500 to-pink-500 text-white"
                    data-testid="result-back-btn">
                    Pick another puzzle <Sparkles className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * One puzzle cell at its (row, col). Crayon-style fill + black border.
 * Trap cells render with an Oops! face. Finish renders the trophy.
 */
function PuzzleCell({ node, isCurrent, isVisited }) {
  const tl = cellTopLeft(node.row, node.col);
  const cx = tl.x + CELL_W / 2;
  const cy = tl.y + CELL_H / 2;
  const paint = CELL_PAINT[node.color] || CELL_PAINT.yellow;
  const problem = node.is_finish ? '' : (node.problem || '');
  const len = problem.length;
  const fontPx = node.is_finish ? 16 : len > 28 ? 13 : len > 18 ? 15 : 18;

  return (
    <g data-testid={`puzzle-node-${node.id}`} style={{ opacity: isVisited ? 0.6 : 1 }}>
      {isCurrent && (
        <motion.rect
          x={tl.x - 5} y={tl.y - 5} width={CELL_W + 10} height={CELL_H + 10} rx={20} ry={20}
          fill="none" stroke="#FB7185" strokeWidth={4}
          animate={{ opacity: [0.35, 0.95, 0.35], scale: [1, 1.04, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        />
      )}
      <rect x={tl.x} y={tl.y} width={CELL_W} height={CELL_H} rx={14} ry={14}
        fill={paint.fill} stroke={paint.stroke} strokeWidth={3}
        filter="url(#crayon-shadow)" />
      <foreignObject x={tl.x + 6} y={tl.y + 6} width={CELL_W - 12} height={CELL_H - 12}>
        <div xmlns="http://www.w3.org/1999/xhtml"
          style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '14px 8px 6px', boxSizing: 'border-box',
            pointerEvents: 'none',
          }}>
          <div style={{
            fontFamily: 'system-ui, sans-serif', fontSize: '10px',
            fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', opacity: 0.55,
            color: paint.text, marginBottom: '4px',
          }}>
            {node.label || ''}
          </div>
          {node.is_finish ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '36px', lineHeight: 1 }}>🏆</div>
              <div style={{
                fontFamily: 'system-ui, sans-serif', fontSize: '13px',
                fontWeight: 800, color: paint.text, marginTop: '2px',
              }}>
                Treasure!
              </div>
            </div>
          ) : (
            <div style={{
              fontFamily: 'ui-monospace, "SF Mono", "Courier New", monospace',
              fontSize: `${fontPx}px`, fontWeight: 800, color: paint.text,
              textAlign: 'center', lineHeight: 1.3,
              wordBreak: 'break-word', overflowWrap: 'anywhere',
              whiteSpace: 'normal', maxWidth: '100%',
            }}>
              {problem}
            </div>
          )}
        </div>
      </foreignObject>
      {isCurrent && !node.is_finish && (
        <g>
          <rect x={tl.x + CELL_W - 36} y={tl.y - 11} width={34} height={20} rx={10} fill="#F43F5E" />
          <text x={tl.x + CELL_W - 19} y={tl.y + 3} textAnchor="middle" fontSize={11}
            fontWeight="800" fill="white" style={{ fontFamily: 'system-ui' }}>
            YOU
          </text>
        </g>
      )}
      {isVisited && !node.is_finish && (
        <g>
          <circle cx={tl.x + CELL_W - 12} cy={tl.y - 4} r={11} fill="#10B981" />
          <text x={tl.x + CELL_W - 12} y={tl.y} textAnchor="middle" fontSize={13}
            fontWeight="800" fill="white" style={{ fontFamily: 'system-ui' }}>
            ✓
          </text>
        </g>
      )}
    </g>
  );
}

/**
 * Strip between two cells. The strip starts at the source-cell EDGE (not center)
 * pointing toward the target-cell EDGE so it doesn't visually overlap into the
 * cell rectangles. Direction is determined by the (row, col) deltas — guaranteed
 * to be one of: right (dx=+1,dy=0), down (dx=0,dy=+1), or diag-down-right (dx=+1,dy=+1).
 *
 * Active states:
 *  - isOutgoingFromCurrent → full color, pulsing yellow aura, value bubble visible.
 *  - isTraversed           → green tint, value visible (the trail).
 *  - default               → faded grey, no value (just hints at the maze topology).
 */
function PuzzleStrip({ edge, fromNode, toNode, paint, isOutgoingFromCurrent, isShaking, traversalState, onClick }) {
  const dr = toNode.row - fromNode.row;
  const dc = toNode.col - fromNode.col;
  const sTL = cellTopLeft(fromNode.row, fromNode.col);
  const tTL = cellTopLeft(toNode.row, toNode.col);

  // Compute strip endpoints — start AT the edge of the source cell, end AT the edge of the target cell.
  // For diagonal: corner-to-corner.
  let sX, sY, eX, eY;
  if (dr === 0 && dc === 1) {        // RIGHT
    sX = sTL.x + CELL_W; sY = sTL.y + CELL_H / 2;
    eX = tTL.x;          eY = tTL.y + CELL_H / 2;
  } else if (dr === 1 && dc === 0) { // DOWN
    sX = sTL.x + CELL_W / 2; sY = sTL.y + CELL_H;
    eX = tTL.x + CELL_W / 2; eY = tTL.y;
  } else if (dr === 1 && dc === 1) { // DIAGONAL DOWN-RIGHT
    sX = sTL.x + CELL_W;     sY = sTL.y + CELL_H;
    eX = tTL.x;              eY = tTL.y;
  } else {
    sX = sTL.x + CELL_W / 2; sY = sTL.y + CELL_H / 2;
    eX = tTL.x + CELL_W / 2; eY = tTL.y + CELL_H / 2;
  }

  const midX = (sX + eX) / 2;
  const midY = (sY + eY) / 2;
  const dx = eX - sX;
  const dy = eY - sY;
  const dist = Math.hypot(dx, dy);
  const angle = Math.atan2(dy, dx) * 180 / Math.PI;

  // Visual states driven by traversalState:
  //   'right'    — kid picked correctly (green strip + green bubble)
  //   'wrong'    — kid picked decoy (red strip + red bubble — the mistake stays visible)
  //   'revealed' — sibling strip (the correct one) revealed AFTER a wrong sibling pick (green strip + green bubble)
  //   'idle'     — not yet visited; faded grey, no value bubble
  // 'isOutgoingFromCurrent' overrides idle to give the active cell's strips full color + value bubble.
  let stripColor, circleStroke, opacity, stripWidth;
  let showValue = false;
  if (traversalState === 'right') {
    stripColor = '#10B981';     // emerald-500
    circleStroke = '#059669';
    opacity = 0.9;
    stripWidth = 24;
    showValue = true;
  } else if (traversalState === 'wrong') {
    stripColor = '#EF4444';     // red-500
    circleStroke = '#B91C1C';
    opacity = 0.95;
    stripWidth = 24;
    showValue = true;
  } else if (traversalState === 'revealed') {
    stripColor = '#10B981';     // emerald — "this WAS the right one"
    circleStroke = '#047857';
    opacity = 0.85;
    stripWidth = 22;
    showValue = true;
  } else if (isOutgoingFromCurrent) {
    stripColor = paint;
    circleStroke = '#1F2937';
    opacity = 1;
    stripWidth = 26;
    showValue = true;
  } else {
    stripColor = '#94A3B8';     // slate idle
    circleStroke = '#1F2937';
    opacity = 0.32;
    stripWidth = 18;
    showValue = false;
  }

  return (
    <motion.g
      data-testid={`puzzle-edge-${edge.id}`}
      onClick={onClick}
      animate={isShaking ? { x: [0, -7, 7, -5, 5, -2, 0] } : {}}
      transition={{ duration: 0.55 }}
      style={{ cursor: isOutgoingFromCurrent ? 'pointer' : 'default', pointerEvents: 'visiblePainted' }}>
      <g transform={`translate(${midX}, ${midY}) rotate(${angle})`}>
        {/* Pulsing aura on active outgoing strips */}
        {isOutgoingFromCurrent && traversalState === 'idle' && (
          <motion.rect
            x={-dist / 2 - 4} y={-stripWidth / 2 - 6}
            width={dist + 8} height={stripWidth + 12} rx={14}
            fill="none" stroke="#FBBF24" strokeWidth={3}
            animate={{ opacity: [0.25, 0.85, 0.25] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
        )}
        {/* Pulsing emerald aura on revealed (right answer reveal) — soft + slow */}
        {traversalState === 'revealed' && (
          <motion.rect
            x={-dist / 2 - 4} y={-stripWidth / 2 - 6}
            width={dist + 8} height={stripWidth + 12} rx={14}
            fill="none" stroke="#34D399" strokeWidth={3}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
        )}
        {/* Outer black crayon outline */}
        <rect x={-dist / 2} y={-stripWidth / 2 - 2}
          width={dist} height={stripWidth + 4} rx={(stripWidth + 4) / 2}
          fill="#1F2937" opacity={opacity} />
        {/* Inner colored strip */}
        <rect x={-dist / 2 + 1} y={-stripWidth / 2}
          width={dist - 2} height={stripWidth} rx={stripWidth / 2}
          fill={stripColor} opacity={opacity} />
      </g>
      {showValue && (
        <g pointerEvents="none">
          {(() => {
            // Ask 2 (Phase 2.28) — grow the bubble to fit long words like
            // "Moral lesson", "Mitochondria", "Antagonist". The previous fixed
            // r=18 circle truncated anything longer than ~4 chars. We now
            // render a pill-shaped rounded rect that sizes to text length,
            // with a monospace-estimated character width and a shrinking
            // font size for >8-char values.
            const valStr = String(edge.value ?? '');
            const n = valStr.length;
            const fontSize = n <= 4 ? 14 : n <= 7 ? 13 : n <= 12 ? 11 : 10;
            const charW = n <= 4 ? 10 : n <= 7 ? 8.5 : n <= 12 ? 7.0 : 6.0;
            const padX = 10;
            const bubbleW = Math.max(36, n * charW + padX * 2);
            const bubbleH = 30;
            return (
              <>
                <rect x={midX - bubbleW / 2} y={midY - bubbleH / 2}
                  width={bubbleW} height={bubbleH} rx={bubbleH / 2} ry={bubbleH / 2}
                  fill="white" stroke={circleStroke} strokeWidth={2.5} />
                <text x={midX} y={midY + 4} textAnchor="middle" fontSize={fontSize}
                  fontWeight="800"
                  fill={traversalState === 'wrong' ? '#B91C1C' : (traversalState === 'right' || traversalState === 'revealed') ? '#047857' : '#0F172A'}
                  style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
                  {valStr}
                </text>
              </>
            );
          })()}
        </g>
      )}
    </motion.g>
  );
}

function ResultPill({ icon: Icon, label, value, accent, testid }) {
  const map = {
    amber:   'from-amber-100 to-amber-50 text-amber-700 border-amber-200',
    rose:    'from-rose-100 to-rose-50 text-rose-700 border-rose-200',
    emerald: 'from-emerald-100 to-emerald-50 text-emerald-700 border-emerald-200',
    slate:   'from-slate-100 to-slate-50 text-slate-600 border-slate-200',
  };
  return (
    <div data-testid={testid} className={`px-2 py-2 rounded-xl border bg-gradient-to-br ${map[accent] || map.slate}`}>
      <Icon className="w-4 h-4 mx-auto mb-1" />
      <p className="text-[9px] font-heading uppercase opacity-70">{label}</p>
      <p className="font-display font-bold text-sm">{value}</p>
    </div>
  );
}
